import { Button, Checkbox, DatePicker, Empty, Flex, Modal, Spin, Timeline, TimelineItemProps, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useOccurrences } from 'src/hooks/useOccurrences';
import { Observer } from 'src/interfaces/entities/observer';
import { StyledTag, StyledObserverName, StyledTimelineContainer } from 'src/components/ObserverHistory/styles';
import { Occurrence } from 'src/interfaces/entities/occurrence';
import { HistoryOutlined } from '@ant-design/icons';

type CheckAlertType = {
	checked?: boolean,
	indeterminate: boolean,
};

export default function ObserverHistory({ observer, forMantine, }: {
	observer: Observer | Occurrence['observer'] & { observer_id: string },
	forMantine?: boolean
}) {
	const {
		period,
		setOccurrencesPeriod,
		isLoading,
		occurrences,
	} = useOccurrences();

	const [ checkAlert, setCheckAlert, ] = useState<CheckAlertType>({
		indeterminate: true,
	});
	const [ open, setOpen, ] = useState<boolean>(false);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleChangeCheckAlert = () => {
		setCheckAlert(prev => {
			if (prev.indeterminate) {
				return { checked: false, indeterminate: false, };
			} else if (prev.checked) {
				return { checked: false, indeterminate: true, };
			}
			return { checked: true, indeterminate: false, };
		});
	};

	const timelineItems = useMemo(() => {
		const lastOccurrences = occurrences
			.sort((a, b) => {
				return dayjs(b.creation_date).valueOf() - dayjs(a.creation_date).valueOf();
			});

		const observerOccurrences = lastOccurrences
			.filter(occ =>
				(
					checkAlert.indeterminate
						? true : checkAlert.checked
							? !occ.is_recovery : occ.is_recovery
				)
                && occ.observer_id === observer.observer_id
			);

		const items: TimelineItemProps[] = observerOccurrences.map(occ => {
			const datetime = dayjs(occ.creation_date)
				.format('DD MMM YYYY, HH:mm');

			return {
				key: occ.ocurrence_id,
				color: occ.is_recovery ? 'green' : occ.observer.severity.color,
				position: occ.is_recovery ? 'left' : 'right',
				label: datetime,
			};
		});

		return items;
	}, [ occurrences, observer.observer_id, checkAlert, ]);

	return (
		<>
			{forMantine
				? <Tooltip title="Histórico">
					<Button type="text" onClick={handleOpen} icon={<HistoryOutlined />} />
				</Tooltip>
				: <Button
					size='small'
					icon={<HistoryOutlined />}
					onClick={handleOpen}
				/>
			}
			<Modal
				open={open}
				title='Histórico do observador'
				footer={null}
				width={600}
				onCancel={handleClose}
			>
				<Flex vertical gap={'16px'}>
					<Flex wrap='wrap' align='center' gap={'16px'}>
						<DatePicker.RangePicker
							onChange={(dates) => {
								if (dates === null) return;
								const inf_date = dates[0];
								const sup_date = dates[1];

								if (!inf_date || !sup_date) return;
								setOccurrencesPeriod(inf_date, sup_date);
							}}
							maxDate={dayjs()}
							format={'DD/MM/YY'}
							value={[
								dayjs(period.inf_date),
								dayjs(period.sup_date),
							]}
						/>
						<Checkbox
							checked={checkAlert.checked}
							indeterminate={checkAlert.indeterminate}
							onChange={handleChangeCheckAlert}
						>
							{
								checkAlert.checked
									? 'Mostando alertas'
									: !checkAlert.indeterminate
                                    && 'Mostando recuperações'
							}
							{checkAlert.indeterminate && 'Mostrando tudo'}
						</Checkbox>
					</Flex>
					<hr />
					<Flex vertical gap={12}>
						<Flex vertical>
							<StyledObserverName>
								{observer.name}
							</StyledObserverName>
							<Flex>
								{observer.tags.map((tag, index) => (
									<StyledTag
										color='geekblue'
										key={index}
									>
										{tag}
									</StyledTag>
								))}
							</Flex>
						</Flex>
						{observer.custom_preview_message}
					</Flex>
					<hr />
					<StyledTimelineContainer>
						{
							isLoading
								? <Spin />
								: occurrences.length > 0
									? <Timeline
										items={timelineItems}
									/>
									: <Empty />
						}
					</StyledTimelineContainer>
				</Flex>
			</Modal>
		</>
	);
}
